import React, { useState } from "react";
import { Member, MemberWithRole } from "@shared/interfaces/User";
import useRoleColor from "@shared/components/ShareProject/utils/useRoleColor";

interface SharedWithNameProps {
  shared_with_name: MemberWithRole[];
  shared_with?: never;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  size?: number;
}

interface SharedWithProps {
  shared_with: Member[];
  shared_with_name?: never;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  size?: number;
}

type AvatarListProps = SharedWithNameProps | SharedWithProps;

const getInitial = (name: string) => name.charAt(0).toUpperCase();

const AvatarData: React.FC<{ name: string; email: string }> = ({
  name,
  email,
}) => (
  <div className="bg-[#021123] text-white font-semibold text-sm rounded-lg px-3 py-2 whitespace-nowrap absolute top-[120%] mb-1 left-1/2 -translate-x-1/2 z-50 shadow">
    <p className="mb-0 leading-tight">{name}</p>
    {email && (
      <p className="text-[0.6rem] text-gray-300 leading-tight">{email}</p>
    )}
  </div>
);

function isMember(user: Member | MemberWithRole): user is Member {
  return (user as Member).first_name !== undefined;
}

function isMemberWithRole(
  user: Member | MemberWithRole
): user is MemberWithRole {
  return (user as MemberWithRole).user !== undefined;
}

const AvatarList: React.FC<AvatarListProps> = ({
  shared_with_name,
  shared_with,
  onClick,
  size = 8,
}) => {
  const users =
    shared_with_name ?? shared_with?.sort((a, b) => a.id - b.id) ?? [];
  const visibleUsers = users.slice(0, 3).map((user) => ({
    user,
    bgColor: useRoleColor(user.ordering),
  }));
  const remainingUsers = users.slice(3);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <div className="flex items-center space-x-[-10px]">
      {visibleUsers.map(({ user, bgColor }, index) => {
        let name = "";
        let email = "";

        if (isMember(user)) {
          name = `${user.first_name} ${user.last_name}`;
          email = user.user_email;
        } else if (isMemberWithRole(user)) {
          name = user.user;
          email = (user as any).email ?? ""; // In case you re-add `email`
        }

        return (
          <div
            key={index}
            className="relative"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {hoveredIndex === index && <AvatarData name={name} email={email} />}
            <div
              className={`w-${size} h-${size} flex items-center justify-center rounded-full text-white font-bold text-md ${bgColor} border-2 border-white transition-transform duration-200 ease-in-out hover:scale-110 cursor-pointer`}
              onClick={onClick}
            >
              {getInitial(name)}
            </div>
          </div>
        );
      })}

      {remainingUsers.length > 0 && (
        <div className="relative group">
          <div className="bg-[#021123] text-white font-semibold text-sm rounded-lg px-3 py-2 hidden group-hover:block whitespace-nowrap absolute top-[120%] mb-1 left-1/2 -translate-x-1/2 z-50 shadow">
            <ul className="list-disc pl-4 space-y-1 text-left">
              {remainingUsers.map((user, idx) => {
                const name = isMember(user)
                  ? `${user.first_name} ${user.last_name}`
                  : user.user;
                const email = isMember(user)
                  ? user.user_email
                  : (user as any).email ?? "";

                return (
                  <li key={idx}>
                    <p>{name}</p>
                    {email && (
                      <p className="text-[0.6rem] text-gray-300">{email}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div
            className={`w-${size} h-${size} flex items-center justify-center text-sm rounded-full bg-[#c7c7c7] text-white font-bold text-md border-2 border-white cursor-pointer transition-transform duration-200 ease-in-out hover:scale-110`}
            onClick={onClick}
          >
            +{remainingUsers.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarList;
